"use client";

import { c as _c } from "react/compiler-runtime";
import { publicEnv } from "@/config/env";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { Capacitor } from "@capacitor/core";
import { Share } from "@capacitor/share";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCopyToClipboard } from "react-use";
const DEFAULT_FEEDBACK_TIMEOUT_MS = 1500;
type WaveLinkActionMode = "share" | "copy";
type WaveLinkActionFeedback = "idle" | "shared" | "copied";
type FeedbackForUrl = {
  readonly url: string;
  readonly value: Exclude<WaveLinkActionFeedback, "idle">;
} | null;
interface UseWaveShareCopyActionParams {
  readonly waveId: string;
  readonly waveName: string;
  readonly isDirectMessage: boolean;
  readonly feedbackTimeoutMs?: number | undefined;
}
interface UseWaveShareCopyActionResult {
  readonly mode: WaveLinkActionMode;
  readonly label: string;
  readonly feedbackState: WaveLinkActionFeedback;
  readonly onClick: () => void;
}
function getErrorName(error: unknown): string {
  if (typeof error === "object" && error !== null && "name" in error && typeof error.name === "string") {
    return error.name;
  }
  return "";
}
function canUseNativeShare(url: string): boolean {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
    return false;
  }
  const shareNavigator = navigator as Navigator & {
    canShare?: ((data?: ShareData) => boolean) | undefined;
  };
  if (typeof shareNavigator.canShare !== "function") {
    return true;
  }
  try {
    return shareNavigator.canShare({
      url
    });
  } catch {
    return false;
  }
}
export function useWaveShareCopyAction(t0) {
  const $ = _c(24);
  const {
    waveId,
    waveName,
    isDirectMessage,
    feedbackTimeoutMs: t1
  } = t0;
  const feedbackTimeoutMs = t1 === undefined ? DEFAULT_FEEDBACK_TIMEOUT_MS : t1;
  const feedbackTimeoutRef = useRef(null);
  const [, copyToClipboard] = useCopyToClipboard();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Capacitor.isNativePlatform();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const isNativeApp = t2;
  let t3;
  if ($[1] !== isDirectMessage || $[2] !== waveId) {
    t3 = getWaveRoute({
      waveId,
      isDirectMessage,
      isApp: false
    });
    $[1] = isDirectMessage;
    $[2] = waveId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const relativeRoute = t3;
  let t4;
  bb0: {
    if (isNativeApp) {
      t4 = `${publicEnv.BASE_ENDPOINT}${relativeRoute}`;
      break bb0;
    }
    if (typeof window === "undefined") {
      t4 = relativeRoute;
      break bb0;
    }
    t4 = `${window.location.origin}${relativeRoute}`;
  }
  const canonicalWaveUrl = t4;
  const nativeShareSupported = isNativeApp || canUseNativeShare(canonicalWaveUrl);
  const [shareFailedUrl, setShareFailedUrl] = useState(null);
  const [feedbackForUrl, setFeedbackForUrl] = useState(null);
  const feedbackState = feedbackForUrl?.url === canonicalWaveUrl ? feedbackForUrl.value : "idle";
  const mode = nativeShareSupported && shareFailedUrl !== canonicalWaveUrl ? "share" : "copy";
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => () => {
      if (feedbackTimeoutRef.current !== null) {
        window.clearTimeout(feedbackTimeoutRef.current);
      }
    };
    t6 = [];
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  if ($[6] !== canonicalWaveUrl || $[7] !== feedbackTimeoutMs) {
    t7 = nextFeedback => {
      if (feedbackTimeoutRef.current !== null) {
        window.clearTimeout(feedbackTimeoutRef.current);
      }
      const targetUrl = canonicalWaveUrl;
      setFeedbackForUrl({
        url: targetUrl,
        value: nextFeedback
      });
      feedbackTimeoutRef.current = window.setTimeout(() => {
        setFeedbackForUrl(currentFeedback => {
          if (currentFeedback?.url !== targetUrl) {
            return currentFeedback;
          }
          return null;
        });
        feedbackTimeoutRef.current = null;
      }, feedbackTimeoutMs);
    };
    $[6] = canonicalWaveUrl;
    $[7] = feedbackTimeoutMs;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const setTemporaryFeedback = t7;
  let t8;
  if ($[9] !== canonicalWaveUrl || $[10] !== copyToClipboard || $[11] !== setTemporaryFeedback) {
    t8 = () => {
      copyToClipboard(canonicalWaveUrl);
      setTemporaryFeedback("copied");
    };
    $[9] = canonicalWaveUrl;
    $[10] = copyToClipboard;
    $[11] = setTemporaryFeedback;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleCopy = t8;
  let t9;
  if ($[13] !== canonicalWaveUrl || $[14] !== handleCopy || $[15] !== mode || $[16] !== setTemporaryFeedback || $[17] !== waveName) {
    t9 = () => {
      if (mode !== "share") {
        handleCopy();
        return;
      }
      (async () => {
        ;
        try {
          if (isNativeApp) {
            await Share.share({
              title: waveName,
              url: canonicalWaveUrl
            });
          } else {
            if (typeof navigator.share === "function") {
              await navigator.share({
                title: waveName,
                url: canonicalWaveUrl
              });
            } else {
              handleCopy();
              return;
            }
          }
          setTemporaryFeedback("shared");
        } catch (t10) {
          const error = t10;
          if (getErrorName(error) === "AbortError") {
            return;
          }
          setShareFailedUrl(canonicalWaveUrl);
          handleCopy();
        }
      })();
    };
    $[13] = canonicalWaveUrl;
    $[14] = handleCopy;
    $[15] = mode;
    $[16] = setTemporaryFeedback;
    $[17] = waveName;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const onClick = t9;
  let label;
  if (feedbackState === "shared") {
    label = "Link shared";
  } else {
    if (feedbackState === "copied") {
      label = "Link copied";
    } else {
      if (mode === "share") {
        label = "Share wave";
      } else {
        label = "Copy wave link";
      }
    }
  }
  let t10;
  if ($[19] !== feedbackState || $[20] !== label || $[21] !== mode || $[22] !== onClick) {
    t10 = {
      mode,
      label,
      feedbackState,
      onClick
    };
    $[19] = feedbackState;
    $[20] = label;
    $[21] = mode;
    $[22] = onClick;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}